import React from "react";
import type { JSX } from "react";
import { Link } from "react-router-dom";
import "./Button.scss";
import { trackEvent } from "@/components/atoms";
import { linkPropsByDevice } from "@/utils";

/**
 * Props for the Button component
 * @interface ButtonProps
 */
interface ButtonProps {
  /** Button type */
  type?: "button" | "submit" | "reset";
  /** Button variant */
  variant?: "primary" | "secondary" | "danger";
  /** Visual fill style */
  appearance?: "solid" | "linear";
  /** Padding and font density tier */
  density?: "compact" | "default" | "spacious" | "block";
  /** Hover/active transform intensity (independent of density) */
  motionScale?: "sm" | "base" | "lg";
  /** Corner radius tier relative to the button scale (base = canonical button radius) */
  borderRadius?: "sm" | "base" | "lg";
  /** Stretch to 100% of parent width */
  fullWidth?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Optional additional CSS class */
  className?: string;
  /** Function executed on click */
  onClick?: () => void;
  /** External link URL (mutually exclusive with `to`) */
  href?: string;
  /** Internal route path (mutually exclusive with `href`) */
  to?: string;
  /** Suffix for GAnalytics tracking (omit to skip tracking) */
  actionTrackSufix?: string;
  /** Accessible label when visible text is insufficient */
  ariaLabel?: string;
  /** Button content */
  children?: React.ReactNode;
}

/**
 * Atomic base button component
 * @component
 * @param {ButtonProps} props - Component props
 * @returns {JSX.Element} Button component
 */
const Button: React.FC<ButtonProps> = ({
  type = "button",
  variant = "primary",
  appearance = "solid",
  density = "default",
  motionScale = "sm",
  borderRadius = "base",
  fullWidth = false,
  disabled = false,
  className = "",
  children,
  onClick,
  href,
  to,
  actionTrackSufix,
  ariaLabel,
}): JSX.Element => {
  const baseClassName = [
    "button",
    `button--${variant}`,
    appearance === "linear" && "button--linear",
    `button--${density}`,
    `button--motion-${motionScale}`,
    borderRadius !== "base" && `button--radius-${borderRadius}`,
    fullWidth && "button--full-width",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const linkA11yProps = disabled
    ? ({ "aria-disabled": true as const, tabIndex: -1 } as const)
    : {};

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    if (disabled) {
      event.preventDefault();
      return;
    }

    if (actionTrackSufix) {
      trackEvent("User Interaction", `click_button_${actionTrackSufix}`);
    }
    onClick?.();
  };

  if (to) {
    return (
      <Link
        to={to}
        className={baseClassName}
        onClick={handleClick}
        aria-label={ariaLabel}
        {...linkA11yProps}
      >
        {children}
      </Link>
    );
  }

  if (href) {
    return (
      <a
        {...(!disabled && { href })}
        className={baseClassName}
        onClick={handleClick}
        aria-label={ariaLabel}
        {...linkA11yProps}
        {...(!disabled && linkPropsByDevice)}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      type={type}
      className={baseClassName}
      disabled={disabled}
      onClick={handleClick}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
};

export default Button;
